import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Target, Plus, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ContributionsPage = () => {
  const [contributions, setContributions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [newContrib, setNewContrib] = useState({
    product_name: '',
    feature_name: '',
    description: '',
    status: 'in_progress',
    employee_id: ''
  });

  const fetchContributions = useCallback(async () => {
    try {
      let url = `${API_URL}/api/contributions`;
      if (statusFilter !== 'all') {
        url += `?status=${statusFilter}`;
      }
      const response = await axios.get(url, { withCredentials: true });
      setContributions(response.data);
    } catch (error) {
      console.error('Fetch contributions error:', error);
      toast.error('Failed to load contributions');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchContributions();
  }, [fetchContributions]);

  const handleAddContribution = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/contributions`, newContrib, { withCredentials: true });
      toast.success('Contribution added');
      setIsAddDialogOpen(false);
      setNewContrib({ product_name: '', feature_name: '', description: '', status: 'in_progress', employee_id: '' });
      fetchContributions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add contribution');
    }
  };

  const getStatusBadge = (status) => {
    const classes = {
      completed: 'badge-success',
      in_progress: 'badge-warning',
      on_hold: 'badge-danger'
    };
    return <Badge className={classes[status]}>{status.replace('_', ' ')}</Badge>;
  };

  // Group by product
  const groupedContributions = contributions.reduce((acc, contrib) => {
    const product = contrib.product_name || 'Other';
    if (!acc[product]) acc[product] = [];
    acc[product].push(contrib);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="contributions-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Target className="w-7 h-7 text-primary" />
            Product Contributions
          </h1>
          <p className="text-muted-foreground">Track features and products employees work on</p>
        </div>
        <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
          <DialogTrigger asChild>
            <Button className="gap-2" data-testid="add-contribution-btn">
              <Plus className="w-4 h-4" />
              Add Contribution
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Contribution</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleAddContribution} className="space-y-4">
              <div className="space-y-2">
                <Label>Product Name</Label>
                <Input
                  value={newContrib.product_name}
                  onChange={(e) => setNewContrib({ ...newContrib, product_name: e.target.value })}
                  placeholder="e.g., WiseVision"
                  required
                  data-testid="contrib-product"
                />
              </div>
              <div className="space-y-2">
                <Label>Feature Name</Label>
                <Input
                  value={newContrib.feature_name}
                  onChange={(e) => setNewContrib({ ...newContrib, feature_name: e.target.value })}
                  placeholder="e.g., Object Detection"
                  required
                  data-testid="contrib-feature"
                />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Input
                  value={newContrib.description}
                  onChange={(e) => setNewContrib({ ...newContrib, description: e.target.value })}
                  placeholder="Brief description..."
                  data-testid="contrib-description"
                />
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={newContrib.status} onValueChange={(v) => setNewContrib({ ...newContrib, status: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="on_hold">On Hold</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setIsAddDialogOpen(false)}>Cancel</Button>
                <Button type="submit" data-testid="submit-contrib">Add</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="in_progress">In Progress</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="on_hold">On Hold</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Products Grid */}
      {Object.keys(groupedContributions).length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {Object.entries(groupedContributions).map(([product, contribs]) => (
            <Card key={product}>
              <CardHeader>
                <CardTitle className="text-lg">{product}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {contribs.map((contrib) => (
                    <div key={contrib.id} className="flex items-start gap-3 p-3 bg-muted rounded-md">
                      <ArrowRight className="w-4 h-4 mt-0.5 text-muted-foreground" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <p className="font-medium text-sm">{contrib.feature_name}</p>
                          {getStatusBadge(contrib.status)}
                        </div>
                        {contrib.description && (
                          <p className="text-xs text-muted-foreground mt-1">{contrib.description}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            No contributions found. Start tracking your work!
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ContributionsPage;
